import React, {
	createContext,
	memo,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	type PropsWithChildren,
} from 'react';
import noop from 'lodash/noop';
import isEqual from 'lodash/isEqual';
import { graphql, useFragment } from 'react-relay';
import memoizeOne from 'memoize-one';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import {
	useEntryPoint,
	type UseEntryPoint,
} from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { ConditionalFormattingEntrypointProvider_view$key } from '@atlassian/jira-relay/src/__generated__/ConditionalFormattingEntrypointProvider_view.graphql';
import { serialiseJql } from '@atlassian/jira-business-filters/src/controllers/utils.tsx';
import { formatRulesEntryPoint } from '../../ui/format-rules-sidebar/sidebar/entrypoint.tsx';

type Props = {
	projectKey: string;
	view: ConditionalFormattingEntrypointProvider_view$key | null;
};

type EntryPoint = UseEntryPoint<typeof formatRulesEntryPoint>;
type ContextValue = {
	getEntryPointActions: () => EntryPoint['entryPointActions'];
	entryPointReferenceSubject: EntryPoint['entryPointReferenceSubject'];
};
const defaultEntrypointActions = {
	cancelPreload: noop,
	load: noop,
	preload: noop,
	unload: noop,
};

export const memoSerialiseJql = memoizeOne(serialiseJql, isEqual);

const ConditionalFormattingEntryPointContext = createContext<ContextValue>({
	getEntryPointActions: () => defaultEntrypointActions,
	entryPointReferenceSubject: {
		getValue: () => null,
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		subscribe: () => () => {},
	},
});
type SerialiseJqlInput = { [key: string]: { label: string; value: string | null }[] };

export const ConditionalFormattingEntrypointProvider = ({
	children,
	// Passing key instead of extracting from fragment to not need to handle null case
	projectKey,
	view,
}: PropsWithChildren<Props>): React.JSX.Element => {
	const viewData = useFragment<ConditionalFormattingEntrypointProvider_view$key>(
		graphql`
			fragment ConditionalFormattingEntrypointProvider_view on JiraSpreadsheetView
			@argumentDefinitions(first: { type: "Int!" }) {
				conditionalFormattingRules(first: $first) {
					edges {
						node {
							expression {
								__typename
								... on JiraFormattingSingleValueOperand {
									fieldId
									value
								}
								... on JiraFormattingMultipleValueOperand {
									fieldId
									values
								}
								... on JiraFormattingNoValueOperand {
									fieldId
								}
							}
						}
					}
				}
			}
		`,
		view,
	);

	const hydrateJql = useMemo(() => {
		const conditionalFormattingRules = viewData?.conditionalFormattingRules;

		if (!conditionalFormattingRules) {
			return '';
		}

		if ((conditionalFormattingRules?.edges?.length ?? 0) === 0) {
			return '';
		}

		const rulesJqlInput: SerialiseJqlInput = {};

		conditionalFormattingRules.edges?.forEach((edge) => {
			const rule = edge?.node;
			if (rule == null) {
				return;
			}
			let fieldId = null;
			let value;
			switch (rule.expression.__typename) {
				case 'JiraFormattingSingleValueOperand':
					({ value, fieldId } = rule.expression);
					break;
				case 'JiraFormattingMultipleValueOperand':
					value = rule.expression.values;
					fieldId = rule.expression.fieldId;
					break;
				case 'JiraFormattingNoValueOperand':
					fieldId = rule.expression.fieldId;
					break;
				default:
					break;
			}

			if (fieldId == null) {
				return;
			}

			// input to serialise jql input
			const DUMMY_VALUE = '-';
			if (!rulesJqlInput[fieldId]) rulesJqlInput[fieldId] = [];
			const values = Array.isArray(value) ? value : [value];
			values.forEach((ruleValue) => {
				if (fieldId)
					rulesJqlInput[fieldId]?.push({
						value: ruleValue ?? DUMMY_VALUE,
						label: ruleValue ?? DUMMY_VALUE,
					});
			});
		});

		const jql = fg('jira_list_conditional_formatting_memo_serialized')
			? memoSerialiseJql(rulesJqlInput)
			: serialiseJql(rulesJqlInput);

		if (jql !== '') {
			return `project = '${projectKey}' AND ${jql}`;
		}
		return '';
	}, [projectKey, viewData?.conditionalFormattingRules]);

	const cloudId = useCloudId();

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(formatRulesEntryPoint, {
		cloudId,
		hydrateJql,
	});

	const entryPointActionsRef = useRef(entryPointActions);

	useEffect(() => {
		entryPointActionsRef.current = entryPointActions;
	}, [entryPointActions]);

	const getEntryPointActions = useCallback(() => {
		return entryPointActionsRef.current;
	}, []);

	const value = useMemo<ContextValue>(
		() => ({
			entryPointReferenceSubject,
			getEntryPointActions,
		}),
		[entryPointReferenceSubject, getEntryPointActions],
	);

	return (
		<ConditionalFormattingEntryPointContext.Provider value={value}>
			{children}
		</ConditionalFormattingEntryPointContext.Provider>
	);
};

const MemoConditionalFormattingEntrypointProvider = memo(ConditionalFormattingEntrypointProvider);

export const MaybeMemoConditionalFormattingEntryPointProvider = componentWithCondition(
	() => fg('jira_list_conditional_formatting_memo_serialized'),
	MemoConditionalFormattingEntrypointProvider,
	ConditionalFormattingEntrypointProvider,
);

export const useConditionalFormattingEntryPoint = () =>
	useContext(ConditionalFormattingEntryPointContext);
